/**
 * Curated Wikidata lexicons. These give the deterministic path high precision on
 * common relations/classes without a round-trip, and let superlatives ("tallest",
 * "most populous") map to a property + sort direction. Live `wbsearchentities`
 * search is the fallback when a phrase isn't here.
 *
 * Keys are normalized surface forms (lowercase, singular-ish). Add rows freely.
 */

export interface PropertyEntry {
  pid: string;
  label: string;
}

export interface ClassEntry {
  qid: string;
  label: string;
}

export interface SuperlativeEntry {
  pid: string;
  label: string;
  descending: boolean;
}

/** relation phrase → Wikidata property. */
export const PROPERTY_LEXICON: Record<string, PropertyEntry> = {
  "capital": { pid: "P36", label: "capital" },
  "capital of": { pid: "P1376", label: "capital of" },
  "population": { pid: "P1082", label: "population" },
  "country": { pid: "P17", label: "country" },
  "continent": { pid: "P30", label: "continent" },
  "located in": { pid: "P131", label: "located in the administrative territorial entity" },
  "location": { pid: "P276", label: "location" },
  "instance of": { pid: "P31", label: "instance of" },
  "is a": { pid: "P31", label: "instance of" },
  "type of": { pid: "P31", label: "instance of" },
  "subclass of": { pid: "P279", label: "subclass of" },
  "director": { pid: "P57", label: "director" },
  "directed by": { pid: "P57", label: "director" },
  "screenwriter": { pid: "P58", label: "screenwriter" },
  "written by": { pid: "P50", label: "author" },
  "author": { pid: "P50", label: "author" },
  "composer": { pid: "P86", label: "composer" },
  "composed by": { pid: "P86", label: "composer" },
  "performer": { pid: "P175", label: "performer" },
  "cast member": { pid: "P161", label: "cast member" },
  "starring": { pid: "P161", label: "cast member" },
  "producer": { pid: "P162", label: "producer" },
  "publication date": { pid: "P577", label: "publication date" },
  "release date": { pid: "P577", label: "publication date" },
  "date of birth": { pid: "P569", label: "date of birth" },
  "born": { pid: "P569", label: "date of birth" },
  "date of death": { pid: "P570", label: "date of death" },
  "died": { pid: "P570", label: "date of death" },
  "place of birth": { pid: "P19", label: "place of birth" },
  "born in": { pid: "P19", label: "place of birth" },
  "place of death": { pid: "P20", label: "place of death" },
  "spouse": { pid: "P26", label: "spouse" },
  "married to": { pid: "P26", label: "spouse" },
  "child": { pid: "P40", label: "child" },
  "children": { pid: "P40", label: "child" },
  "father": { pid: "P22", label: "father" },
  "mother": { pid: "P25", label: "mother" },
  "sibling": { pid: "P3373", label: "sibling" },
  "occupation": { pid: "P106", label: "occupation" },
  "employer": { pid: "P108", label: "employer" },
  "educated at": { pid: "P69", label: "educated at" },
  "member of": { pid: "P463", label: "member of" },
  "genre": { pid: "P136", label: "genre" },
  "official language": { pid: "P37", label: "official language" },
  "language": { pid: "P37", label: "official language" },
  "currency": { pid: "P38", label: "currency" },
  "inception": { pid: "P571", label: "inception" },
  "founded": { pid: "P571", label: "inception" },
  "established": { pid: "P571", label: "inception" },
  "founder": { pid: "P112", label: "founder" },
  "headquarters": { pid: "P159", label: "headquarters location" },
  "area": { pid: "P2046", label: "area" },
  "elevation": { pid: "P2044", label: "elevation above sea level" },
  "height": { pid: "P2048", label: "height" },
  "length": { pid: "P2043", label: "length" },
  "part of": { pid: "P361", label: "part of" },
  "has part": { pid: "P527", label: "has part" },
  "shares border with": { pid: "P47", label: "shares border with" },
  "chief executive officer": { pid: "P169", label: "chief executive officer" },
  "ceo": { pid: "P169", label: "chief executive officer" },
  "official website": { pid: "P856", label: "official website" },
  "named after": { pid: "P138", label: "named after" },
  "award received": { pid: "P166", label: "award received" },
  "religion": { pid: "P140", label: "religion or worldview" },
  "sport": { pid: "P641", label: "sport" },
};

/** class noun → Wikidata class, used for `?x wdt:P31 <class>` type constraints. */
export const CLASS_LEXICON: Record<string, ClassEntry> = {
  "film": { qid: "Q11424", label: "film" },
  "movie": { qid: "Q11424", label: "film" },
  "book": { qid: "Q571", label: "book" },
  "novel": { qid: "Q8261", label: "novel" },
  "country": { qid: "Q6256", label: "country" },
  "city": { qid: "Q515", label: "city" },
  "town": { qid: "Q3957", label: "town" },
  "person": { qid: "Q5", label: "human" },
  "human": { qid: "Q5", label: "human" },
  "mountain": { qid: "Q8502", label: "mountain" },
  "river": { qid: "Q4022", label: "river" },
  "lake": { qid: "Q23397", label: "lake" },
  "company": { qid: "Q783794", label: "company" },
  "university": { qid: "Q3918", label: "university" },
  "painting": { qid: "Q3305213", label: "painting" },
  "song": { qid: "Q7366", label: "song" },
  "album": { qid: "Q482994", label: "album" },
  "video game": { qid: "Q7889", label: "video game" },
  "language": { qid: "Q34770", label: "language" },
  "planet": { qid: "Q634", label: "planet" },
  "element": { qid: "Q11344", label: "chemical element" },
};

/** superlative adjective → property + sort direction. */
export const SUPERLATIVE_LEXICON: Record<string, SuperlativeEntry> = {
  "largest": { pid: "P2046", label: "area", descending: true },
  "biggest": { pid: "P2046", label: "area", descending: true },
  "smallest": { pid: "P2046", label: "area", descending: false },
  "most populous": { pid: "P1082", label: "population", descending: true },
  "most populated": { pid: "P1082", label: "population", descending: true },
  "least populous": { pid: "P1082", label: "population", descending: false },
  "tallest": { pid: "P2044", label: "elevation above sea level", descending: true },
  "highest": { pid: "P2044", label: "elevation above sea level", descending: true },
  "lowest": { pid: "P2044", label: "elevation above sea level", descending: false },
  "longest": { pid: "P2043", label: "length", descending: true },
  "shortest": { pid: "P2043", label: "length", descending: false },
  "oldest": { pid: "P571", label: "inception", descending: false },
  "newest": { pid: "P571", label: "inception", descending: true },
};

function normalize(phrase: string): string {
  return phrase.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Look up a relation phrase, tolerating a trailing plural "s". */
export function lookupProperty(phrase: string): PropertyEntry | undefined {
  const key = normalize(phrase);
  return (
    PROPERTY_LEXICON[key] ??
    (key.endsWith("s") ? PROPERTY_LEXICON[key.slice(0, -1)] : undefined)
  );
}

/** Look up a class noun, tolerating a trailing plural "s". */
export function lookupClass(noun: string): ClassEntry | undefined {
  const key = normalize(noun);
  return (
    CLASS_LEXICON[key] ??
    (key.endsWith("s") ? CLASS_LEXICON[key.slice(0, -1)] : undefined)
  );
}

export function lookupSuperlative(adjective: string): SuperlativeEntry | undefined {
  return SUPERLATIVE_LEXICON[normalize(adjective)];
}
